import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Props {
  email: string | null;
}

const DangerZone = ({ email }: Props) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const canDelete = !!email && confirmEmail.trim().toLowerCase() === email.toLowerCase();

  const handleDelete = async () => {
    if (!canDelete) return;
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("delete-account");
    if (error || !data?.ok) {
      setLoading(false);
      toast({
        title: "Couldn't delete account",
        description: error?.message || data?.error || "Please try again.",
        variant: "destructive",
      });
      return;
    }
    try {
      localStorage.removeItem("saved-furniture-items");
    } catch {
      // ignore
    }
    await supabase.auth.signOut();
    toast({ title: "Account deleted", description: "We've removed your data. Sorry to see you go." });
    navigate("/", { replace: true });
  };

  return (
    <div className="bg-card border border-destructive/40 p-6 w-full space-y-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
        <div>
          <h3 className="text-foreground font-medium">Delete account</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Permanently remove your account, all saved designs, and profile data. This cannot be undone.
          </p>
        </div>
      </div>
      <Button
        variant="destructive"
        className="rounded-full"
        onClick={() => {
          setConfirmEmail("");
          setOpen(true);
        }}
      >
        Delete my account
      </Button>

      <Dialog open={open} onOpenChange={(o) => !loading && setOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete account permanently?</DialogTitle>
            <DialogDescription>
              This deletes your account and every design tied to it. To confirm, type your email
              address below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="confirm-email">Your email</Label>
            <Input
              id="confirm-email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={email ?? ""}
              autoComplete="off"
              disabled={loading}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={!canDelete || loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DangerZone;
